import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Receipt, CheckCircle2, Clock, XCircle, Sparkles } from "lucide-react";

const STATUS_META = {
  SUCCESS: { color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", icon: CheckCircle2, label: "BERJAYA" },
  PENDING: { color: "text-neutral-400", bg: "bg-white/5 border-white/10", icon: Clock, label: "MENUNGGU" },
  FAILED:  { color: "text-red-400", bg: "bg-red-500/10 border-red-500/20", icon: XCircle, label: "GAGAL" },
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/me");
        setItems(data);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalPaid = items.filter((i) => i.status === "SUCCESS").reduce((s, i) => s + (i.price_rm || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">Resit</div>
          <h1 className="font-display text-4xl font-bold">Sejarah Transaksi</h1>
        </div>
        <Link to="/pricing" className="btn-primary inline-flex items-center gap-2" data-testid="pricing-link">
          <Sparkles size={16} /> Beli / Lanjut Akses
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-5">
          <div className="text-xs text-neutral-500 uppercase tracking-wider font-mono">Jumlah Bayaran Berjaya</div>
          <div className="font-display text-3xl font-bold gold-text mt-1">RM{totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-[#0F0F0F] border border-white/10 rounded-xl p-5">
          <div className="text-xs text-neutral-500 uppercase tracking-wider font-mono">Bilangan Transaksi</div>
          <div className="font-display text-3xl font-bold mt-1">{items.length}</div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-center text-neutral-500 py-12">Memuatkan…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-xl" data-testid="tx-empty">
          <Receipt size={32} className="mx-auto text-neutral-600" />
          <p className="text-neutral-400 mt-3">Belum ada transaksi.</p>
          <Link to="/pricing" className="text-amber-400 text-sm hover:underline mt-2 inline-block">
            Beli pakej anda →
          </Link>
        </div>
      ) : (
        <div className="bg-[#101010] border border-white/10 rounded-xl overflow-hidden" data-testid="tx-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#141414] border-b border-white/10 text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Pakej</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Jumlah</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Status</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Tarikh</th>
                <th className="px-5 py-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">Order No.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.PENDING;
                const Icon = meta.icon;
                return (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`tx-row-${it.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-display font-semibold">{it.tier_label}</div>
                      {it.extended_to && (
                        <div className="text-[10px] text-neutral-500 font-mono">→ {it.extended_to}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-amber-400 font-bold">RM{it.price_rm}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border uppercase tracking-wider font-mono ${meta.bg} ${meta.color}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-neutral-500 font-mono">
                      {it.paid_at ? new Date(it.paid_at).toLocaleString("ms-MY") : new Date(it.created_at).toLocaleString("ms-MY")}
                    </td>
                    <td className="px-5 py-3 text-xs text-neutral-400 font-mono">{it.billcode}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
